import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { iApiError } from "../../types/commonTypes";
import {
	IAdminVendorChatDetails,
	IArchivedSummary,
	IChat,
	IChatsData,
	IChatsRecord,
	IGetAllChatsParams,
} from "../../interfaces/chats";
import {
	createAdminVendorChat,
	getAdminVendorChatRecordThunk,
	getAllChatsRecord,
	getChatRecordByEventId,
	getUnreadChannelsCount,
} from "./chatsThunk";
import { DEFAULT_API_ERROR } from "../../constants/commonConstants";
import { CorporateEventVendorStatusEnum, EventTypeFilterEnum } from "../../constants/enums";
import { VendorEventStatusEnum } from "../../types/eventTypes";

export interface IDashboardStore {
	isLoading: boolean;
	chatsRecord: IChatsData | null;
	totalUnreadChannelsCount: number;
	allEvents: IChatsRecord[];
	apiError: iApiError | null;
	latestRequestId: string | null;
	latestUnreadChannelsRequestId: string | null;
	eventType: EventTypeFilterEnum | null;
	adminVendorChatRecord: IAdminVendorChatDetails | null;
	adminVendorChatRecordLoading: boolean;

	/** When true, skip the next Firestore-driven update on the list. Set by Sendbird handlers; consumed by ChatsListPage onEventModified. */
	skipNextFirestoreUpdate: boolean;

	/** When true, skip the next Firestore-driven update on the detail. Set by getDetailsByEventId; consumed by ChatDetailPage Firestore callbacks. */
	skipNextFirestoreUpdateDetail: boolean;

	archivedSummary: IArchivedSummary | null;
	isArchivedInbox: boolean;
	pendingInboxMoves: Record<string, boolean>;
	detachedInboxEvents: Record<string, IChatsRecord>;
	inboxViewCache: Partial<Record<InboxView, { key: string; record: IChatsData }>>;
}

type InboxView = "inbox" | "archived";

const inboxView = (archived: boolean): InboxView => (archived ? "archived" : "inbox");

export const inboxCacheKey = (params: IGetAllChatsParams) => {
	const { page: _page, limit: _limit, silent: _silent, ...rest } = params;
	const query = rest as Record<string, unknown>;
	return JSON.stringify(
		Object.keys(query)
			.filter((key) => query[key] !== undefined)
			.sort()
			.map((key) => [key, query[key]]),
	);
};

type ChatStatePatch = Partial<Pick<IChat, "unreadMessageCount" | "hasUnreadMention">>;

const patchChatRows = (rows: IChat[] | undefined, ids: Set<string>, patch: ChatStatePatch) => {
	rows?.forEach((row, index) => {
		if (ids.has(row.id)) rows[index] = { ...row, ...patch };
	});
};

const activityTime = (event: IChatsRecord) => Date.parse(event.lastActivityAt ?? "") || 0;

const insertByActivity = (events: IChatsRecord[], event: IChatsRecord) => {
	const activity = Date.parse(event.lastActivityAt ?? "") || Number.POSITIVE_INFINITY;
	const index = events.findIndex((existing) => activityTime(existing) < activity);
	events.splice(index === -1 ? events.length : index, 0, event);
};

const withPendingInboxMoves = (state: IDashboardStore, events: IChatsRecord[], archived: boolean, firstPage: boolean) => {
	const moves = state.pendingInboxMoves;
	const kept = events.filter((event) => moves[event.eventId] === undefined || moves[event.eventId] === archived);
	if (!firstPage) return kept;
	Object.entries(moves).forEach(([eventId, toArchived]) => {
		const event = state.detachedInboxEvents[eventId];
		if (toArchived === archived && event && !kept.some((existing) => existing.eventId === eventId)) {
			insertByActivity(kept, event);
		}
	});
	return kept;
};

const placeInboxEvents = (state: IDashboardStore, eventIds: string[], archived: boolean) => {
	if (!state.chatsRecord) return;
	const ids = new Set(eventIds);
	if (archived === state.isArchivedInbox) {
		eventIds.forEach((eventId) => {
			const event = state.detachedInboxEvents[eventId];
			if (event && !state.allEvents.some((existing) => existing.eventId === eventId)) {
				insertByActivity(state.allEvents, event);
			}
		});
	} else {
		state.allEvents = state.allEvents.filter((event) => !ids.has(event.eventId));
	}
	state.chatsRecord.events = state.allEvents;
};

const isListedVendorStatus = (event: IChatsRecord) => {
	const allowedStatus: (CorporateEventVendorStatusEnum | VendorEventStatusEnum)[] =
		event.eventType === EventTypeFilterEnum.B2B
			? [
					CorporateEventVendorStatusEnum.AvailableForBidding,
					CorporateEventVendorStatusEnum.Accepted,
					CorporateEventVendorStatusEnum.Completed,
				]
			: [
					VendorEventStatusEnum.Bidding,
					VendorEventStatusEnum.AvailableForBiddingAiRequest,
					VendorEventStatusEnum.Accepted,
					VendorEventStatusEnum.Completed,
				];
	return allowedStatus.includes(event.vendorEventStatus as CorporateEventVendorStatusEnum | VendorEventStatusEnum);
};

const initialState: IDashboardStore = {
	isLoading: false,
	apiError: null,
	chatsRecord: null,
	allEvents: [],
	latestRequestId: null,
	totalUnreadChannelsCount: 0,
	latestUnreadChannelsRequestId: null,
	eventType: null,
	adminVendorChatRecord: null,
	adminVendorChatRecordLoading: false,

	skipNextFirestoreUpdate: false,
	skipNextFirestoreUpdateDetail: false,

	archivedSummary: null,
	isArchivedInbox: false,
	pendingInboxMoves: {},
	detachedInboxEvents: {},
	inboxViewCache: {},
};

const chatsSlice = createSlice({
	name: "dashboard",
	initialState,
	reducers: {
		resetApiErrorDashboard(state) {
			state.apiError = null;
		},
		resetChatsData(state) {
			state.allEvents = [];
			state.chatsRecord = null;
			state.eventType = null;
		},
		setEventType(state, action) {
			state.eventType = action.payload;
		},
		setAdminVendorChatRecord(state, action) {
			state.adminVendorChatRecord = action.payload;
		},
		setSkipNextFirestoreUpdate(state, action) {
			state.skipNextFirestoreUpdate = action.payload;
		},
		setSkipNextFirestoreUpdateDetail(state, action) {
			state.skipNextFirestoreUpdateDetail = action.payload;
		},
		switchInboxView(state, action: PayloadAction<{ archived: boolean; key: string; openEventId?: string }>) {
			const { archived, key, openEventId } = action.payload;
			const openEvent = state.allEvents.find((event) => event.eventId === openEventId);
			if (openEvent) state.detachedInboxEvents[openEvent.eventId] = openEvent;
			const current = state.inboxViewCache[inboxView(state.isArchivedInbox)];
			if (state.chatsRecord && current) current.record = state.chatsRecord;
			const cached = state.inboxViewCache[inboxView(archived)];
			if (cached && cached.key === key) {
				state.allEvents = withPendingInboxMoves(state, cached.record.events, archived, true);
				state.chatsRecord = { ...cached.record, events: state.allEvents };
				state.isArchivedInbox = archived;
			} else {
				state.allEvents = [];
				state.chatsRecord = null;
			}
		},
		moveInboxEvents(state, action: PayloadAction<{ eventIds: string[]; toArchived: boolean }>) {
			const { eventIds, toArchived } = action.payload;
			const ids = new Set(eventIds);
			state.allEvents.forEach((event) => {
				if (ids.has(event.eventId)) state.detachedInboxEvents[event.eventId] = event;
			});
			eventIds.forEach((eventId) => {
				state.pendingInboxMoves[eventId] = toArchived;
			});
			placeInboxEvents(state, eventIds, toArchived);
		},
		settleInboxMoves(state, action: PayloadAction<{ eventIds: string[]; toArchived: boolean; revert?: boolean }>) {
			const { eventIds, toArchived, revert } = action.payload;
			eventIds.forEach((eventId) => {
				if (state.pendingInboxMoves[eventId] === toArchived) delete state.pendingInboxMoves[eventId];
			});
			if (revert) {
				placeInboxEvents(state, eventIds, !toArchived);
				return;
			}
			(["inbox", "archived"] as const).forEach((view) => {
				const entry = state.inboxViewCache[view];
				if (!entry) return;
				const events = entry.record.events.filter((event) => !eventIds.includes(event.eventId));
				if ((view === "archived") === toArchived) {
					eventIds.forEach((eventId) => {
						const event = state.detachedInboxEvents[eventId];
						if (event) insertByActivity(events, event);
					});
				}
				entry.record = { ...entry.record, events };
			});
		},
		applyOpenChatPreview(
			state,
			action: PayloadAction<{
				channelUrl: string;
				lastMessage: string;
				lastMessageAt: number;
				lastMessageId: number;
			}>,
		) {
			const { channelUrl, lastMessage, lastMessageAt, lastMessageId } = action.payload;
			const eventIndex = state.allEvents.findIndex((event) => event.chats.some((chat) => chat.channelUrl === channelUrl));
			if (eventIndex === -1) return;
			const event = state.allEvents[eventIndex];
			const chat = event.chats.find((row) => row.channelUrl === channelUrl);
			if (!chat) return;
			chat.unreadMessageCount = 0;
			chat.hasUnreadMention = false;
			if (lastMessageId > (chat.lastMessageId ?? 0)) {
				chat.lastMessage = lastMessage;
				chat.lastMessageAt = lastMessageAt;
				chat.lastMessageId = lastMessageId;
				event.lastActivityAt = new Date(lastMessageAt).toISOString();
				if (!state.isArchivedInbox && eventIndex > 0) {
					state.allEvents.splice(eventIndex, 1);
					state.allEvents.unshift(event);
				}
			}
			if (state.chatsRecord) state.chatsRecord.events = state.allEvents;
		},
		patchChatState(state, action: PayloadAction<{ chatIds: string[]; patch: ChatStatePatch }>) {
			const ids = new Set(action.payload.chatIds);
			const { patch } = action.payload;
			state.allEvents.forEach((event) => patchChatRows(event.chats, ids, patch));
			Object.values(state.detachedInboxEvents).forEach((event) => patchChatRows(event.chats, ids, patch));
			if (state.chatsRecord) {
				state.chatsRecord.events = state.allEvents;
			}
			const support = state.adminVendorChatRecord;
			if (support && ids.has(support.id)) {
				if (patch.unreadMessageCount !== undefined) support.unreadMessageCount = patch.unreadMessageCount;
				if (patch.hasUnreadMention !== undefined) support.hasUnreadMention = patch.hasUnreadMention;
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getAllChatsRecord.pending, (state, action) => {
			if (!action.meta.arg.silent) state.isLoading = true;
			state.latestRequestId = action.meta.requestId;
		});
		builder.addCase(getAllChatsRecord.fulfilled, (state, action) => {
			// Only update if this is the latest request
			if (action.meta.requestId === state.latestRequestId) {
				const currentPage = action.payload.pagination?.currentPage || 1;
				const archived = Boolean(action.meta.arg.archived);
				const payload = {
					...action.payload,
					events: withPendingInboxMoves(state, action.payload.events, archived, currentPage === 1),
				};

				if (currentPage === 1) {
					// First page - replace all events
					state.allEvents = payload.events;
				} else {
					// Subsequent pages - append events
					const existingEventIds = new Set(state.allEvents.map((event) => event.eventId));
					const newEvents = payload.events.filter((event) => !existingEventIds.has(event.eventId));
					state.allEvents = [...state.allEvents, ...newEvents];
				}

				// Update chatsRecord with accumulated events and latest pagination
				state.chatsRecord = {
					...payload,
					events: state.allEvents,
				};
				if (payload.archivedSummary) {
					state.archivedSummary = payload.archivedSummary;
				}
				state.isArchivedInbox = archived;
				state.inboxViewCache[inboxView(archived)] = { key: inboxCacheKey(action.meta.arg), record: state.chatsRecord };
				state.isLoading = false;
				state.latestRequestId = null;
			}
		});
		builder.addCase(getAllChatsRecord.rejected, (state, action) => {
			const isAborted = action.payload?.message === "Request aborted";

			// Only update loading state if this is the latest request
			if (action.meta.requestId === state.latestRequestId) {
				if (!isAborted) {
					// Only set error for non-aborted rejections
					state.apiError = action.payload ?? DEFAULT_API_ERROR;
				}
				state.isLoading = false;
				state.latestRequestId = null;
			}
			// If aborted and not the latest request, don't change anything
		});
		builder.addCase(getUnreadChannelsCount.pending, (state, action) => {
			if (!action.meta.arg.silent) state.isLoading = true;
			state.latestUnreadChannelsRequestId = action.meta.requestId;
		});
		builder.addCase(getUnreadChannelsCount.fulfilled, (state, action) => {
			// Only update if this is the latest request
			if (action.meta.requestId === state.latestUnreadChannelsRequestId) {
				const { payload } = action;
				state.totalUnreadChannelsCount = payload.totalUnreadCount || 0;
				state.isLoading = false;
				state.latestUnreadChannelsRequestId = null;
			}
		});
		builder.addCase(getUnreadChannelsCount.rejected, (state, action) => {
			const isAborted = action.payload?.message === "Request aborted";

			// Only update loading state if this is the latest request
			if (action.meta.requestId === state.latestUnreadChannelsRequestId) {
				if (!isAborted) {
					// Only set error for non-aborted rejections
					state.apiError = action.payload ?? DEFAULT_API_ERROR;
				}
				state.isLoading = false;
				state.latestUnreadChannelsRequestId = null;
			}
			// If aborted and not the latest request, don't change anything
		});
		builder.addCase(getChatRecordByEventId.fulfilled, (state, action) => {
			const payload = action.payload as IChatsData;
			if (!payload?.events?.length) return;

			const updatedByEventId = new Map(payload.events.map((e) => [e.eventId, e]));
			const { listedChatsOnly } = action.meta.arg;
			state.allEvents.forEach((event, i) => {
				const updated = updatedByEventId.get(event.eventId);
				if (!updated) return;
				const listedChatIds = new Set(event.chats.map((chat) => chat.id));
				const chats = updated.chats.filter(
					(chat) =>
						Boolean(chat.isArchived) === state.isArchivedInbox && (!listedChatsOnly || listedChatIds.has(chat.id)),
				);
				if (isListedVendorStatus(updated) && chats.length) {
					state.allEvents[i] = { ...updated, chats };
				} else {
					state.allEvents = state.allEvents.filter((e) => e.eventId !== updated.eventId);
				}
			});

			if (state.chatsRecord) {
				state.chatsRecord.events = state.allEvents;
			}
		});
		builder.addCase(getChatRecordByEventId.rejected, (state, action) => {
			if (action.payload?.message === "Request aborted") return;
		});
		builder.addCase(getAdminVendorChatRecordThunk.pending, (state, action) => {
			state.adminVendorChatRecordLoading = !state.adminVendorChatRecord?.id ? true : false;
		});
		builder.addCase(getAdminVendorChatRecordThunk.fulfilled, (state, action) => {
			state.adminVendorChatRecord = { ...action.payload };
			state.adminVendorChatRecordLoading = false;
		});
		builder.addCase(getAdminVendorChatRecordThunk.rejected, (state, action) => {
			state.adminVendorChatRecordLoading = false;
		});

		builder.addCase(createAdminVendorChat.pending, (state, action) => {
			state.adminVendorChatRecordLoading = true;
		});
		builder.addCase(createAdminVendorChat.fulfilled, (state, action) => {
			state.adminVendorChatRecordLoading = false;
		});
		builder.addCase(createAdminVendorChat.rejected, (state, action) => {
			state.adminVendorChatRecordLoading = false;
		});
	},
});

export const {
	resetApiErrorDashboard,
	resetChatsData,
	setEventType,
	setAdminVendorChatRecord,
	setSkipNextFirestoreUpdate,
	setSkipNextFirestoreUpdateDetail,
	switchInboxView,
	moveInboxEvents,
	settleInboxMoves,
	applyOpenChatPreview,
	patchChatState,
} = chatsSlice.actions;
export default chatsSlice.reducer;
